// Right-hand panel of the Product Master: one form for new and existing
// products, split into Profile / Pricing & tax / Barcodes / Stock & reorder /
// Media tabs. Mapping to and from the API lives in productModel.js.
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSizeSets, getSubCategories } from "../../../../services/SizeColourApiServices";
import { all_routes } from "../../../../Router/all_routes";
import { Printer, Trash2, X, Plus, RotateCcw } from "feather-icons-react/build/IconComponents";
import {
  addNewBrand,
  addNewProductimg,
  getProductStockByWarehouse,
  getWareHouseList,
} from "../../../../services/MasterApiServices";
import { fetchBrand } from "../../../../redux/userSetting";
import { ProductPos } from "../../../../core/json/ProductPos";
import { BrandClass } from "../../../../core/json/BrandClass";
import { pmHue, pmInitials, PmBarcodeIcon, PmTranslateIcon, PmImageIcon, PmGalleryIcon } from "./pmUi";
import {
  BC_TYPES,
  LANGS,
  MAX_IMAGES,
  PRICE_KEYS,
  STOCK_TYPES,
  ean13,
  findByBarcode,
  findByCode,
  findByName,
  fmt,
  grouped,
  localTranslate,
  mkUnitRow,
  num,
  pCode,
  pStock,
  printBarcodeLabel,
  toDraft,
  toFormData,
  validate,
} from "./productModel";

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "pricing", label: "Pricing & tax" },
  { key: "barcodes", label: "Barcodes" },
  { key: "stock", label: "Stock & reorder" },
  { key: "media", label: "Media" },
];

const clone = (d) => ({
  ...d,
  units: d.units.map((u) => ({ ...u })),
  barcodes: d.barcodes.map((b) => ({ ...b })),
  images: [...d.images],
  removedImages: [...d.removedImages],
});

// resize a picked image on a canvas (the old form did the same: 200 / 600 px)
const resizeImage = (file, size) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        canvas.getContext("2d").drawImage(img, 0, 0, size, size);
        canvas.toBlob((blob) => {
          if (!blob) return resolve(null);
          const out = new File([blob], file.name, { type: file.type });
          resolve({ file: out, preview: URL.createObjectURL(out) });
        }, file.type);
      };
      img.onerror = () => resolve(null);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

const ProductDetail = ({ product, productList, permit, onSaved, onClose, onDelete, onDirtyChange }) => {
  const dispatch = useDispatch();
  const { taxList, unitList, groupList, categoryList, deliveyTypes, brandList } = useSelector(
    (s) => s.usersetting
  );
  const { branch } = useSelector((s) => s.branchlist);
  const { userId } = useSelector((s) => s.userauth);
  const scOn = useSelector((s) => !!s.settings?.branchsettings?.enableSizeColour);
  const navigate = useNavigate();

  const isNew = !product;
  const initial = useMemo(
    () => toDraft(product, { taxList, unitList }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [product?.guid]
  );
  const [draft, setDraft] = useState(() => clone(initial));
  const [original, setOriginal] = useState(() => clone(initial));
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState("profile");
  const [msg, setMsg] = useState({ text: "", cls: "" });
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [transLang, setTransLang] = useState("ar");
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [addUnit, setAddUnit] = useState("");
  const [addFactor, setAddFactor] = useState("");
  const [bcInput, setBcInput] = useState("");
  const [stock, setStock] = useState({ rows: null, error: false, loading: false });

  const nameRef = useRef(null);
  const bcRef = useRef(null);
  const tabBodyRef = useRef(null);
  const thumbFileRef = useRef(null);
  const galFileRef = useRef(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    dispatch(fetchBrand());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isNew) setTimeout(() => nameRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Size & Colour: sub-categories of the chosen category, and the size sets
  const [subCats, setSubCats] = useState([]);
  const [sizeSets, setSizeSets] = useState([]);
  useEffect(() => {
    if (!scOn) return;
    getSizeSets()
      .then((r) => setSizeSets(Array.isArray(r) ? r : []))
      .catch(() => setSizeSets([]));
  }, [scOn]);
  useEffect(() => {
    if (!scOn || !draft.category) return setSubCats([]);
    let live = true;
    getSubCategories(draft.category)
      .then((r) => live && setSubCats(Array.isArray(r) ? r : []))
      .catch(() => live && setSubCats([]));
    return () => {
      live = false;
    };
  }, [scOn, draft.category]);
  // a sub-category with its own size set fills it in
  const onSubCategory = (e) => {
    const g = e.target.value;
    const s = subCats.find((x) => x.guid === g);
    update({ subCategory: g, ...(s?.sizeSetID && !draft.sizeSet ? { sizeSet: s.sizeSetID } : {}) });
  };

  const unitName = (g) => unitList?.find((u) => u.guid === g)?.name || "—";
  const taxRate = num(taxList?.find((t) => t.guid === draft.tax)?.percentage);
  const catName = categoryList?.find((c) => c.guid === draft.category)?.name;
  const grpName = groupList?.find((g) => g.guid === draft.group)?.name;

  // groups of the chosen category, when the group record carries one
  const groupOptions = useMemo(() => {
    const catOf = (g) => g?.categoryID ?? g?.category?.guid ?? g?.categoryGuid;
    const tagged = (groupList || []).some((g) => catOf(g));
    if (!tagged || !draft.category) return groupList || [];
    return (groupList || []).filter((g) => catOf(g) === draft.category);
  }, [groupList, draft.category]);

  // ---------- editing ----------
  const touch = () => setDirty(true);
  const update = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    touch();
  };
  const clearErr = (f) => errs[f] && setErrs((e) => ({ ...e, [f]: false }));

  const field = (f, opts = {}) => ({
    value: draft[f] ?? "",
    className: [opts.className, errs[f] ? "err" : ""].filter(Boolean).join(" "),
    onChange: (e) => {
      clearErr(f);
      update({ [f]: e.target.value });
    },
  });

  // live duplicate check on name + code
  useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => {
      const d = draftRef.current;
      const dn = findByName(productList, d);
      const dc = findByCode(productList, d);
      const out = [];
      if (dn) out.push(`Name already used by ${pCode(dn)}`);
      if (dc) out.push(`Code already used by ${dc.name}`);
      setErrs((e) => ({ ...e, name: !!dn, code: !!dc }));
      setMsg({ text: out.join(" · "), cls: out.length ? "err" : "" });
    }, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.name, draft.code]);

  const goTab = (key) => {
    setTab(key);
    if (tabBodyRef.current) tabBodyRef.current.scrollTop = 0;
  };

  // ---------- profile: category, base unit, brand, translation ----------
  const onCategory = (e) => {
    clearErr("category");
    update({ category: e.target.value, group: "", subCategory: "" });
  };
  const onBaseUnit = (e) => {
    const unit = e.target.value;
    clearErr("unit");
    setDraft((d) => {
      const units = d.units.map((u) => ({ ...u }));
      units[0].unit = unit;
      units[0].factor = 1;
      return { ...d, unit, units: units.filter((u, i) => i === 0 || u.unit !== unit) };
    });
    touch();
  };

  const saveBrand = async () => {
    const name = brandName.trim();
    if (!name) return;
    const existing = brandList?.find((b) => b.name?.trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      update({ brand: existing.guid });
      setBrandOpen(false);
      toast.success(`Brand ${existing.name} selected`);
      return;
    }
    try {
      const obj = new BrandClass();
      obj.name = name;
      obj.updatedUser = `{${userId?.id}}`;
      const res = await addNewBrand(obj);
      if (res?.data === true) {
        const list = (await dispatch(fetchBrand()))?.payload || [];
        const made = list.find((b) => b.name?.trim().toLowerCase() === name.toLowerCase());
        if (made) update({ brand: made.guid });
        setBrandOpen(false);
        toast.success(`Brand ${name} added`);
      } else {
        toast.error("Brand was not saved");
      }
    } catch (err) {
      toast.error(err?.response?.data?.Message || "Brand was not saved");
    }
  };

  const convertName = async () => {
    const text = draft.name.trim();
    if (!text) return;
    const key = process.env.REACT_APP_TRANSLATION_API_KEY;
    let out = "";
    if (key) {
      try {
        const r = await axios.post(
          `https://translation.googleapis.com/language/translate/v2?key=${key}`,
          { q: text, target: transLang },
          { requireheader: false }
        );
        out = r?.data?.data?.translations?.[0]?.translatedText || "";
      } catch (e) {
        out = "";
      }
    }
    if (!out) out = localTranslate(text, transLang);
    update({ nameAr: out });
    toast.success(`Name converted to ${LANGS.find((l) => l.code === transLang)?.name}`);
  };

  // ---------- pricing: unit rows ----------
  const setUnits = (fn) => {
    setDraft((d) => ({ ...d, units: fn(d.units.map((u) => ({ ...u }))) }));
    touch();
  };
  const usedUnits = draft.units.map((u) => u.unit);
  const freeUnits = (unitList || []).filter((u) => !usedUnits.includes(u.guid));

  const onUnitCell = (i, key, value) =>
    setUnits((units) => {
      if (key === "factor") units[i].factor = value;
      else units[i][key] = value;
      return units;
    });
  const onFactorCommit = (i) =>
    setUnits((units) => {
      const f = Math.max(1, num(units[i].factor) || 1);
      units[i] = mkUnitRow(units[i].unit, f, units[0]);
      return units;
    });
  const recalcRow = (i) =>
    setUnits((units) => {
      units[i] = mkUnitRow(units[i].unit, num(units[i].factor) || 1, units[0]);
      return units;
    });
  const addConversion = () => {
    const unit = addUnit || freeUnits[0]?.guid;
    const factor = num(addFactor);
    if (!unit) return toast.error("Pick a unit first");
    if (!(factor > 0)) {
      toast.error(`How many ${unitName(draft.units[0].unit)} make one ${unitName(unit)}?`);
      return undefined;
    }
    setUnits((units) => [...units, mkUnitRow(unit, factor, units[0])]);
    setAddFactor("");
    setAddUnit("");
    return undefined;
  };

  const base = draft.units[0];
  const readout = useMemo(() => {
    const b = base || {};
    const sales = num(b.sales);
    const cost = num(b.cost);
    // with "tax included" every price on the row holds the tax, so each margin
    // is worked out on its price less the tax, the same as the retail margin
    const exTax = (v) => (draft.taxIncluded ? num(v) / (1 + taxRate / 100) : num(v));
    const margin = (v) => {
      const n = exTax(v);
      return n > 0 ? ((n - cost) / n) * 100 : 0;
    };
    const net = exTax(sales);
    const withTax = draft.taxIncluded ? sales : sales * (1 + taxRate / 100);
    const m = margin(sales);
    const mw = margin(b.wholesale);
    const mc = margin(b.corporate);
    const mm = margin(b.minPrice);
    const band = (v, warnAt) => (v < 0 ? "bad" : v < warnAt ? "warn" : "good");
    return [
      { k: `Net of tax · ${unitName(b.unit)}`, v: fmt(net) },
      { k: "Customer pays", v: fmt(withTax) },
      { k: "Retail margin", v: `${fmt(m)}%`, c: band(m, 8) },
      { k: "Wholesale margin", v: `${fmt(mw)}%`, c: band(mw, 5) },
      { k: "Corporate margin", v: `${fmt(mc)}%`, c: band(mc, 5) },
      { k: "Min price margin", v: `${fmt(mm)}%`, c: band(mm, 5) },
      { k: "Floor price", v: fmt(b.minPrice), c: num(b.minPrice) > sales ? "bad" : "" },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, draft.taxIncluded, taxRate]);

  // ---------- barcodes ----------
  const setBarcodes = (fn) => {
    setDraft((d) => ({ ...d, barcodes: fn(d.barcodes.map((b) => ({ ...b }))) }));
    touch();
  };
  const addBarcode = (value, type) => {
    const v = String(value).trim();
    if (!v) return;
    if (draft.barcodes.some((b) => b.value.trim() === v)) {
      toast.error("That barcode is already on this product");
      return;
    }
    const clash = findByBarcode(productList, draft, v);
    if (clash) {
      toast.error(`Barcode already used by ${pCode(clash)} — ${clash.name}`);
      return;
    }
    setBarcodes((list) => [
      ...list,
      { value: v, type: type || "EAN-13", unit: draft.unit, pack: 1, primary: list.length === 0 },
    ]);
  };
  const onBcCell = (i, key, value) => {
    setBarcodes((list) => {
      list[i][key] = key === "pack" ? Math.max(1, parseInt(value, 10) || 1) : value;
      return list;
    });
    if (key === "value") {
      const v = value.trim();
      const clash = findByBarcode(productList, draft, v);
      const twice = draft.barcodes.some((b, j) => j !== i && b.value.trim() === v);
      setErrs((e) => ({ ...e, [`bc${i}`]: !!clash || twice }));
      setMsg({
        text: clash
          ? `Barcode belongs to ${pCode(clash)} — ${clash.name}`
          : twice
          ? "That barcode is already on this product"
          : "",
        cls: clash || twice ? "err" : "",
      });
    }
  };
  const removeBarcode = (i) =>
    setBarcodes((list) => {
      const wasPrimary = list[i].primary;
      list.splice(i, 1);
      if (wasPrimary && list.length) list[0].primary = true;
      return list;
    });

  // ---------- stock ----------
  useEffect(() => {
    if (isNew || !product?.guid) return undefined;
    let live = true;
    setStock({ rows: null, error: false, loading: true });
    getProductStockByWarehouse(product.guid, branch)
      .then((data) => {
        if (!live) return;
        const rows = (Array.isArray(data) ? data : []).map((r) => ({
          wh: r.warehouseName ?? r.vanName ?? r.name ?? "—",
          onHand: num(r.onHand ?? r.quantity ?? r.stock),
          committed: num(r.committed ?? r.committedQty),
        }));
        setStock({ rows, error: false, loading: false });
      })
      .catch(async () => {
        if (!live) return;
        // endpoint not deployed yet: list the warehouses with the branch total
        let whs = [];
        try {
          const w = await getWareHouseList();
          whs = Array.isArray(w) ? w : [];
        } catch (e) {
          whs = [];
        }
        if (live) setStock({ rows: null, error: true, loading: false, whs });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.guid, branch?.guid]);

  const stockRows = stock.rows || [];
  const listStock = pStock(product);
  const tot = stock.rows ? stockRows.reduce((s, r) => s + r.onHand, 0) : listStock ?? 0;
  const com = stockRows.reduce((s, r) => s + r.committed, 0);
  const maxQty = num(draft.maxQty);
  const reorderAt = num(draft.reorderLevel);
  const fillPct = maxQty > 0 ? Math.min(100, (tot / maxQty) * 100) : 0;

  // ---------- media ----------
  const pickThumb = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const img = await resizeImage(f, 200);
    if (!img) return toast.error("That file could not be read as an image");
    update({ thumb: img });
    return undefined;
  };
  const pickImages = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_IMAGES - draft.images.length;
    if (room <= 0) return toast.error(`${MAX_IMAGES} images is the limit — remove one first`);
    const imgs = (await Promise.all(picked.slice(0, room).map((f) => resizeImage(f, 600)))).filter(Boolean);
    setDraft((d) => ({ ...d, images: [...d.images, ...imgs] }));
    touch();
    const overflow = picked.length - room;
    if (overflow > 0)
      toast.error(`${overflow} image${overflow > 1 ? "s" : ""} skipped — the limit is ${MAX_IMAGES}`);
    return undefined;
  };
  const removeImage = (i) => {
    setDraft((d) => {
      const gone = d.images[i];
      return {
        ...d,
        images: d.images.filter((_, j) => j !== i),
        removedImages: gone?.path ? [...d.removedImages, gone.path] : d.removedImages,
      };
    });
    touch();
  };

  // ---------- save / reset / delete ----------
  const save = async () => {
    if (saving) return;
    const d = draftRef.current;
    const problem = validate(d, productList, unitName);
    if (problem) {
      setMsg({ text: problem.msg, cls: "err" });
      if (problem.fields) setErrs((e) => ({ ...e, ...Object.fromEntries(problem.fields.map((f) => [f, true])) }));
      goTab(problem.tab);
      if (problem.tab === "barcodes" && !d.barcodes.length) setTimeout(() => bcRef.current?.focus(), 0);
      return;
    }
    setSaving(true);
    try {
      const res = await addNewProductimg(toFormData(d, { taxList, branch, userId, ProductPos, sizeColour: scOn }));
      if (res === true) {
        toast.success(`Product ${d.code} ${isNew ? "created" : "saved"}`);
        setOriginal(clone(d));
        setDirty(false);
        setMsg({ text: "", cls: "" });
        onSaved?.(d.code.trim(), d.name.trim());
      } else {
        const why = res?.Message || res?.message;
        setMsg({ text: why || "The product was not saved", cls: "err" });
        toast.error(why || "The product was not saved");
      }
    } catch (err) {
      const data = err?.response?.data;
      const why =
        data?.Message ||
        data?.message ||
        data?.title ||
        (typeof data === "string" ? data : "") ||
        "The product was not saved";
      setMsg({ text: why, cls: "err" });
      toast.error(why);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setDraft(clone(original));
    setDirty(false);
    setErrs({});
    setMsg({ text: "", cls: "" });
    toast(isNew ? "Form cleared" : "Changes discarded");
  };

  // label price: the barcode's own unit row, else base price × pack qty
  const labelPrice = (b) => {
    const row = draft.units.find((u) => u.unit === b.unit);
    return fmt(row ? num(row.sales) : num(base.sales) * (num(b.pack) || 1));
  };

  // F9 saves, Esc discards
  const saveRef = useRef(save);
  const resetRef = useRef(reset);
  const dirtyRef = useRef(dirty);
  saveRef.current = save;
  resetRef.current = reset;
  dirtyRef.current = dirty;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F9") {
        e.preventDefault();
        saveRef.current();
      } else if (e.key === "Escape" && dirtyRef.current && !document.querySelector(".swal2-container")) {
        e.preventDefault();
        resetRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ---------- render ----------
  const pill = isNew ? ["new", "New"] : dirty ? ["dirty", "Unsaved"] : ["saved", "Saved"];
  const subtitle =
    (draft.code || "no code yet") + (catName ? `  ·  ${catName}${grpName ? ` › ${grpName}` : ""}` : "");

  const sel = (f, options, placeholder, extra = {}) => (
    <select
      id={`pm-${f}`}
      {...field(f)}
      {...extra}
    >
      <option value="">{placeholder}</option>
      {options}
    </select>
  );

  return (
    <section className="pm-detail">
      <div className="pm-dhead">
        <span className="pm-thumb" style={{ "--h": pmHue(draft.category || draft.name) }}>
          {draft.thumb?.preview ? <img src={draft.thumb.preview} alt="" /> : pmInitials(draft.name)}
        </span>
        <div className="pm-dtitle">
          <div className="pm-t">{draft.name || "New product"}</div>
          <div className="pm-s">{subtitle}</div>
        </div>
        <span className={`pm-pill ${pill[0]}`}>{pill[1]}</span>
        <button type="button" className="pm-close" onClick={onClose} aria-label="Close">
          <X />
        </button>
      </div>

      <nav className="pm-tabnav" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            className="pm-tab"
            aria-selected={tab === t.key}
            onClick={() => goTab(t.key)}
          >
            {t.label}
            {t.key === "barcodes" && <span className="n">{draft.barcodes.length}</span>}
          </button>
        ))}
      </nav>

      <div className="pm-tabbody" ref={tabBodyRef}>
        {/* ---------------- PROFILE ---------------- */}
        {tab === "profile" && (
          <div className="pm-formgrid">
            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-name">
                Name<span className="pm-req">*</span>
              </label>
              <input
                id="pm-name"
                ref={nameRef}
                placeholder="Trade name as printed on the invoice"
                {...field("name")}
              />
            </div>
            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-nameAr">
                Name — other language
              </label>
              <div className="pm-inrow">
                <input
                  id="pm-nameAr"
                  className="pm-ar"
                  dir={transLang === "ar" ? "rtl" : "auto"}
                  placeholder="الاسم بالعربية"
                  value={draft.nameAr}
                  onChange={(e) => update({ nameAr: e.target.value })}
                />
                <select
                  className="pm-langsel"
                  aria-label="Other language"
                  value={transLang}
                  onChange={(e) => setTransLang(e.target.value)}
                >
                  {LANGS.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="pm-tbtn"
                  disabled={!draft.name.trim()}
                  onClick={convertName}
                  title="Convert the name into the selected language"
                >
                  <PmTranslateIcon />
                  Convert
                </button>
              </div>
              <span className="pm-hint">
                {transLang === "ar"
                  ? "Arabic uses the product dictionary, then transliterates anything it does not know."
                  : "Transliterated into the target script — edit it by hand if you need a true translation."}
              </span>
            </div>

            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-code">
                Code<span className="pm-req">*</span>
              </label>
              <input id="pm-code" placeholder="BEV-0114" {...field("code", { className: "pm-mono" })} />
            </div>
            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-brand">
                Brand
              </label>
              <div className="pm-inrow">
                {sel(
                  "brand",
                  (brandList || []).map((b) => (
                    <option key={b.guid} value={b.guid}>
                      {b.name}
                    </option>
                  )),
                  "Select brand"
                )}
                <button
                  type="button"
                  className="pm-iconsq add"
                  title="Add a new brand"
                  aria-label="Add a new brand"
                  onClick={() => {
                    setBrandName("");
                    setBrandOpen((o) => !o);
                  }}
                >
                  <Plus />
                </button>
              </div>
              {brandOpen && (
                <div className="pm-brandnew">
                  <input
                    autoFocus
                    placeholder="New brand name"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveBrand();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();
                        setBrandOpen(false);
                      }
                    }}
                  />
                  <button type="button" className="go" onClick={saveBrand}>
                    Add
                  </button>
                  <button type="button" onClick={() => setBrandOpen(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-category">
                Category<span className="pm-req">*</span>
              </label>
              {sel(
                "category",
                (categoryList || []).map((c) => (
                  <option key={c.guid} value={c.guid}>
                    {c.name}
                  </option>
                )),
                "Select category",
                { onChange: onCategory }
              )}
            </div>
            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-group">
                Group<span className="pm-req">*</span>
              </label>
              {sel(
                "group",
                groupOptions.map((g) => (
                  <option key={g.guid} value={g.guid}>
                    {g.name}
                  </option>
                )),
                "Select group"
              )}
            </div>
            {scOn && (
              <div className="pm-f">
                <label className="pm-flabel" htmlFor="pm-subCategory">
                  Sub-category
                </label>
                <select id="pm-subCategory" value={draft.subCategory || ""} onChange={onSubCategory} disabled={!draft.category}>
                  <option value="">{draft.category ? (subCats.length ? "None" : "No sub-categories") : "Pick a category first"}</option>
                  {subCats.map((s) => (
                    <option key={s.guid} value={s.guid}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-itemType">
                Item type<span className="pm-req">*</span>
              </label>
              {sel(
                "itemType",
                (deliveyTypes || []).map((t) => (
                  <option key={t.guid} value={t.guid}>
                    {t.name}
                  </option>
                )),
                "Select item type"
              )}
            </div>
            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-stockType">
                Stock type<span className="pm-req">*</span>
              </label>
              {sel(
                "stockType",
                STOCK_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                )),
                "Select stock type"
              )}
            </div>

            <div className="pm-f">
              <label className="pm-flabel" htmlFor="pm-unit">
                Base unit<span className="pm-req">*</span>
              </label>
              {sel(
                "unit",
                (unitList || []).map((u) => (
                  <option key={u.guid} value={u.guid}>
                    {u.name}
                  </option>
                )),
                "Select unit",
                { onChange: onBaseUnit }
              )}
            </div>
            <div className="pm-f">
              <span className="pm-flabel">Flags</span>
              <div className="pm-inline">
                <label className="pm-switch">
                  <input
                    type="checkbox"
                    checked={!!draft.quickItem}
                    onChange={(e) => update({ quickItem: e.target.checked })}
                  />
                  <span className="pm-track" />
                  Quick item
                </label>
                <label className="pm-switch">
                  <input
                    type="checkbox"
                    checked={!!draft.active}
                    onChange={(e) => update({ active: e.target.checked })}
                  />
                  <span className="pm-track" />
                  Active
                </label>
                {scOn && (
                  <label className="pm-switch">
                    <input
                      type="checkbox"
                      checked={!!draft.sizeColour}
                      onChange={(e) => {
                        const on = e.target.checked;
                        const fromSub = subCats.find((x) => x.guid === draft.subCategory)?.sizeSetID;
                        update({ sizeColour: on, ...(on && !draft.sizeSet && fromSub ? { sizeSet: fromSub } : {}) });
                      }}
                    />
                    <span className="pm-track" />
                    Sizes &amp; colours
                  </label>
                )}
              </div>
            </div>
            {scOn && draft.sizeColour && (
              <div className="pm-f">
                <label className="pm-flabel" htmlFor="pm-sizeSet">
                  Size set
                </label>
                <select id="pm-sizeSet" value={draft.sizeSet || ""} onChange={(e) => update({ sizeSet: e.target.value })}>
                  <option value="">Select size set</option>
                  {sizeSets.map((s) => (
                    <option key={s.guid} value={s.guid}>
                      {s.name} ({(s.sizes || []).map((z) => z.code).join(" ")})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {scOn && draft.sizeColour && (
              <div className="pm-f">
                <span className="pm-flabel">Variants</span>
                <div className="pm-inline">
                  <span>
                    {draft.variants.length
                      ? `${draft.variants.length} variants · ${draft.variants.filter((x) => x.isActive !== false).length} active`
                      : "No variants yet"}
                  </span>
                  <button
                    type="button"
                    className="pm-tbtn"
                    disabled={isNew || dirty}
                    title={isNew || dirty ? "Save the product first" : "Create sizes, colours, SKUs and barcodes"}
                    onClick={() => navigate(`${all_routes.setsizecolour}?product=${draft.guid}`)}
                  >
                    Set size &amp; colour
                  </button>
                </div>
              </div>
            )}

            <div className="pm-f full">
              <label className="pm-flabel" htmlFor="pm-description">
                Detailed description
              </label>
              <textarea
                id="pm-description"
                placeholder="Shown on the web shop and the printed catalogue"
                value={draft.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* ---------------- PRICING ---------------- */}
        {tab === "pricing" && (
          <>
            <div className="pm-secthead">Units and price list</div>
            <div className="pm-uptwrap">
              <table className="pm-upt">
                <thead>
                  <tr>
                    <th style={{ width: 118 }}>Unit</th>
                    <th style={{ width: 118 }}>Conversion</th>
                    <th className="r">Cost</th>
                    <th className="r">Sales</th>
                    <th className="r">Wholesale</th>
                    <th className="r">Corporate</th>
                    <th className="r">Min selling</th>
                    <th style={{ width: 58 }} />
                  </tr>
                </thead>
                <tbody>
                  {draft.units.map((u, i) => {
                    const isBase = i === 0;
                    const perBase = num(u.factor) > 0 ? num(u.sales) / num(u.factor) : 0;
                    const above = !isBase && num(base.sales) > 0 && perBase > num(base.sales) + 0.004;
                    return (
                      <tr key={i} className={isBase ? "base" : ""}>
                        <td>
                          {isBase ? (
                            <div className="pm-uname">
                              {unitName(u.unit)}
                              <span className="pm-ubadge">Base</span>
                            </div>
                          ) : (
                            <select
                              aria-label="Unit"
                              value={u.unit}
                              onChange={(e) => onUnitCell(i, "unit", e.target.value)}
                            >
                              {(unitList || [])
                                .filter((x) => x.guid === u.unit || !usedUnits.includes(x.guid))
                                .map((x) => (
                                  <option key={x.guid} value={x.guid}>
                                    {x.name}
                                  </option>
                                ))}
                            </select>
                          )}
                        </td>
                        <td>
                          {isBase ? (
                            <span className="pm-fac">1 × {unitName(u.unit)}</span>
                          ) : (
                            <span className="pm-fac">
                              <input
                                className="n"
                                inputMode="numeric"
                                aria-label="Units per base unit"
                                value={u.factor}
                                onChange={(e) => onUnitCell(i, "factor", e.target.value)}
                                onBlur={() => onFactorCommit(i)}
                              />
                              <span>× {unitName(base.unit)}</span>
                            </span>
                          )}
                        </td>
                        {PRICE_KEYS.map((k) => (
                          <td key={k}>
                            <input
                              className="n"
                              inputMode="decimal"
                              aria-label={k}
                              value={u[k]}
                              onChange={(e) => onUnitCell(i, k, e.target.value)}
                              onBlur={(e) => onUnitCell(i, k, fmt(num(e.target.value)))}
                            />
                            {k === "sales" && !isBase && (
                              <span className={"pm-per" + (above ? " bad" : "")}>
                                {fmt(perBase)} / {unitName(base.unit)}
                              </span>
                            )}
                          </td>
                        ))}
                        <td>
                          {!isBase && (
                            <div className="pm-rowacts">
                              <button
                                type="button"
                                className="pm-rowbtn"
                                title="Recalculate from the base unit"
                                aria-label="Recalculate from base"
                                onClick={() => recalcRow(i)}
                              >
                                <RotateCcw />
                              </button>
                              <button
                                type="button"
                                className="pm-rowbtn del"
                                title="Remove unit"
                                aria-label="Remove unit"
                                onClick={() => setUnits((units) => units.filter((_, j) => j !== i))}
                              >
                                <Trash2 />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pm-uptadd">
              <select
                aria-label="Conversion unit"
                value={addUnit}
                onChange={(e) => setAddUnit(e.target.value)}
                disabled={!freeUnits.length}
              >
                <option value="">{freeUnits.length ? "Unit" : "All units used"}</option>
                {freeUnits.map((u) => (
                  <option key={u.guid} value={u.guid}>
                    {u.name}
                  </option>
                ))}
              </select>
              <input
                inputMode="numeric"
                placeholder="× base"
                aria-label="Units per base unit"
                value={addFactor}
                onChange={(e) => setAddFactor(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addConversion();
                  }
                }}
              />
              <button type="button" onClick={addConversion} disabled={!freeUnits.length || !base.unit}>
                Add conversion unit
              </button>
              <span className="pm-hint">
                {base.unit
                  ? "Prices are per unit. A conversion row starts at base × factor — overwrite any cell to hold a negotiated rate."
                  : "Pick a base unit on the Profile tab first."}
              </span>
            </div>

            <div className="pm-secthead gap">Tax</div>
            <div className="pm-formgrid">
              <div className="pm-f">
                <label className="pm-flabel" htmlFor="pm-tax">
                  Tax<span className="pm-req">*</span>
                </label>
                {sel(
                  "tax",
                  (taxList || []).map((t) => (
                    <option key={t.guid} value={t.guid}>
                      {t.name}
                    </option>
                  )),
                  "Select tax"
                )}
              </div>
              <div className="pm-f">
                <span className="pm-flabel">Tax handling</span>
                <label className="pm-switch">
                  <input
                    type="checkbox"
                    checked={!!draft.taxIncluded}
                    onChange={(e) => update({ taxIncluded: e.target.checked })}
                  />
                  <span className="pm-track" />
                  Tax included in sales price
                </label>
              </div>
            </div>

            <div className="pm-readout">
              {readout.map((t) => (
                <div key={t.k} className={"pm-rdo" + (t.c ? ` ${t.c}` : "")}>
                  <div className="k">{t.k}</div>
                  <div className="v">{t.v}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ---------------- BARCODES ---------------- */}
        {tab === "barcodes" && (
          <>
            <div className="pm-bcadd">
              <div className="grow">
                <PmBarcodeIcon />
                <input
                  ref={bcRef}
                  autoComplete="off"
                  placeholder="Scan or type a barcode, then press Enter"
                  value={bcInput}
                  onChange={(e) => setBcInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const v = bcInput.trim();
                    addBarcode(v, /^\d{13}$/.test(v) ? "EAN-13" : "Supplier");
                    setBcInput("");
                  }}
                />
              </div>
              <button type="button" onClick={() => addBarcode(ean13(), "EAN-13")}>
                <Plus />
                Generate EAN-13
              </button>
            </div>

            <table className="pm-bc">
              <colgroup>
                <col style={{ width: 58 }} />
                <col />
                <col style={{ width: 108 }} />
                <col style={{ width: 96 }} />
                <col style={{ width: 72 }} />
                <col style={{ width: 66 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="c">Main</th>
                  <th>Barcode</th>
                  <th>Type</th>
                  <th>Unit</th>
                  <th>Pack qty</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.barcodes.map((b, i) => (
                  <tr key={i}>
                    <td className="pm-radio">
                      <input
                        type="radio"
                        name="pmBcPrimary"
                        checked={!!b.primary}
                        aria-label="Main barcode"
                        onChange={() => setBarcodes((list) => list.map((x, j) => ({ ...x, primary: i === j })))}
                      />
                    </td>
                    <td>
                      <input
                        className={"mono" + (errs[`bc${i}`] ? " err" : "")}
                        value={b.value}
                        onChange={(e) => onBcCell(i, "value", e.target.value)}
                      />
                    </td>
                    <td>
                      <select value={b.type} onChange={(e) => onBcCell(i, "type", e.target.value)}>
                        {BC_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={b.unit || ""} onChange={(e) => onBcCell(i, "unit", e.target.value)}>
                        <option value="">—</option>
                        {(unitList || []).map((u) => (
                          <option key={u.guid} value={u.guid}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="mono r"
                        inputMode="numeric"
                        value={b.pack}
                        onChange={(e) => onBcCell(i, "pack", e.target.value)}
                      />
                    </td>
                    <td>
                      <div className="pm-rowacts">
                        <button
                          type="button"
                          className="pm-rowbtn"
                          title="Print label"
                          aria-label="Print label"
                          onClick={() =>
                            b.value.trim()
                              ? printBarcodeLabel({
                                  value: b.value.trim(),
                                  name: draft.name,
                                  price: labelPrice(b),
                                })
                              : toast.error("Enter the barcode first")
                          }
                        >
                          <Printer />
                        </button>
                        <button
                          type="button"
                          className="pm-rowbtn del"
                          title="Remove"
                          aria-label="Remove barcode"
                          onClick={() => removeBarcode(i)}
                        >
                          <Trash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!draft.barcodes.length && (
              <div className="pm-bcempty">No barcode yet — scan one above, or generate an EAN-13.</div>
            )}
            <div className="pm-note">
              A product can carry as many barcodes as it needs — supplier code, retail pack, carton,
              and any re-labelled stock. The one marked Main prints on the label.
            </div>
          </>
        )}

        {/* ---------------- STOCK ---------------- */}
        {tab === "stock" && (
          <>
            <div className="pm-secthead">Reorder rules</div>
            <div className="pm-formgrid">
              {[
                ["minQty", "Minimum quantity"],
                ["maxQty", "Maximum quantity"],
                ["reorderLevel", "Reorder level"],
                ["reorderQty", "Reorder quantity"],
              ].map(([f, label]) => (
                <div className="pm-f" key={f}>
                  <label className="pm-flabel" htmlFor={`pm-${f}`}>
                    {label}
                    <span className="pm-req">*</span>
                  </label>
                  <input id={`pm-${f}`} inputMode="decimal" {...field(f, { className: "pm-num" })} />
                  {f === "reorderLevel" && (
                    <span className="pm-hint">Triggers a purchase suggestion when stock drops here.</span>
                  )}
                </div>
              ))}
            </div>

            <div className="pm-secthead gap">Stock on hand · {branch?.name || "this branch"}</div>
            <table className="pm-wh">
              <thead>
                <tr>
                  <th>Warehouse</th>
                  <th className="r">On hand</th>
                  <th className="r">Committed</th>
                  <th className="r">Free</th>
                </tr>
              </thead>
              <tbody>
                {isNew && (
                  <tr>
                    <td colSpan={4} className="muted">
                      A new product starts with no stock.
                    </td>
                  </tr>
                )}
                {stock.loading && (
                  <tr>
                    <td colSpan={4} className="muted">
                      Loading…
                    </td>
                  </tr>
                )}
                {stockRows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.wh}</td>
                    <td className="r">{grouped(r.onHand)}</td>
                    <td className="r">{grouped(r.committed)}</td>
                    <td className="r">{grouped(r.onHand - r.committed)}</td>
                  </tr>
                ))}
                {stock.error &&
                  (stock.whs || []).map((w) => (
                    <tr key={w.guid || w.id}>
                      <td>{w.name || w.vanName}</td>
                      <td className="r muted">—</td>
                      <td className="r muted">—</td>
                      <td className="r muted">—</td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className="r">{listStock === null && !stock.rows ? "—" : grouped(tot)}</td>
                  <td className="r">{stock.rows ? grouped(com) : "—"}</td>
                  <td className="r">{stock.rows ? grouped(tot - com) : "—"}</td>
                </tr>
              </tfoot>
            </table>
            <div className={"pm-bar" + (tot <= 0 ? " bad" : tot <= reorderAt ? " warn" : "")}>
              <i style={{ width: `${fillPct}%` }} />
            </div>
            <div className="pm-note">
              {stock.error
                ? "Per-warehouse stock is not available from the server yet — the total comes from the product list."
                : maxQty > 0
                ? `${grouped(tot)} of a ${grouped(maxQty)} ceiling · reorder at ${grouped(reorderAt)}` +
                  (tot <= reorderAt ? " — due for a purchase order" : "")
                : "Set a maximum quantity to see the fill level."}
            </div>
          </>
        )}

        {/* ---------------- MEDIA ---------------- */}
        {tab === "media" && (
          <>
            <div className="pm-formgrid">
              <div className="pm-f">
                <span className="pm-flabel">Thumbnail image</span>
                <div className="pm-drop" onClick={() => thumbFileRef.current?.click()} role="button" tabIndex={0}>
                  <span className="pm-ph">
                    {draft.thumb?.preview ? <img src={draft.thumb.preview} alt="Thumbnail" /> : <PmImageIcon />}
                  </span>
                  <span className="pm-lbl">{draft.thumb ? "Replace the thumbnail" : "Drop an image, or"}</span>
                  <span className="pm-pick">Choose file</span>
                  <span className="pm-sz">150 × 150 px · PNG or JPG</span>
                  <input ref={thumbFileRef} type="file" accept="image/*" onChange={pickThumb} />
                </div>
                {draft.thumb && (
                  <button type="button" className="pm-linkbtn" onClick={() => update({ thumb: null })}>
                    Remove thumbnail
                  </button>
                )}
              </div>
              <div className="pm-f">
                <span className="pm-flabel">Product detail images</span>
                <div
                  className={"pm-drop" + (draft.images.length >= MAX_IMAGES ? " is-disabled" : "")}
                  onClick={() => draft.images.length < MAX_IMAGES && galFileRef.current?.click()}
                  role="button"
                  tabIndex={0}
                >
                  <span className="pm-ph">
                    <PmGalleryIcon />
                  </span>
                  <span className="pm-lbl">Up to {MAX_IMAGES} images</span>
                  <span className="pm-pick">Add images</span>
                  <span className="pm-sz">550 × 550 px · PNG or JPG · pick several at once</span>
                  <input ref={galFileRef} type="file" accept="image/*" multiple onChange={pickImages} />
                </div>
              </div>
            </div>
            <div className="pm-gallery">
              {draft.images.map((img, i) => (
                <span key={img.path || img.preview} className="pm-gitem">
                  <img src={img.preview} alt={`Detail ${i + 1}`} />
                  {i === 0 && <span className="pm-cover">Cover</span>}
                  <button
                    type="button"
                    className="pm-x"
                    aria-label={`Remove image ${i + 1}`}
                    onClick={() => removeImage(i)}
                  >
                    <X />
                  </button>
                </span>
              ))}
              {draft.images.length < MAX_IMAGES && (
                <button type="button" className="pm-gadd" onClick={() => galFileRef.current?.click()}>
                  <Plus />
                  <span>Add</span>
                </button>
              )}
            </div>
            <div className="pm-gcount">
              {draft.images.length} of {MAX_IMAGES} images
              {draft.images.length ? " · the first one is the cover" : ""}
            </div>
          </>
        )}
      </div>

      <div className="pm-dfoot">
        <span className={"pm-msg" + (msg.cls ? ` ${msg.cls}` : "")}>
          {msg.text || "Fields marked * are required"}
        </span>
        <span className="pm-sp" />
        {!isNew && permit?.deletion && (
          <button type="button" className="pm-btn pm-btn-danger" onClick={() => onDelete?.(product)}>
            Delete
          </button>
        )}
        <button type="button" className="pm-btn pm-btn-ghost" onClick={reset}>
          Reset
        </button>
        <button type="button" className="pm-btn pm-btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"} <span className="pm-kbd">F9</span>
        </button>
      </div>
    </section>
  );
};

export default ProductDetail;
